import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../lib/auth-context';
import { TravelProfile } from '../types/matching';
import { doc, getDoc, updateDoc, deleteDoc, arrayUnion, arrayRemove, Timestamp } from 'firebase/firestore';
import { db, storage } from '../lib/firebase';
import { MessageSquare, Trash2, Heart, Calendar, MapPin, Clock, Briefcase, GraduationCap, Shield, Users, Wallet, Globe as Globe2, User, ExternalLink } from 'lucide-react';
import { CreateListingForm } from '../components/matching/CreateListingForm';
import { ProfileHeader } from '../components/matching/profile/ProfileHeader';
import { ProfileHero } from '../components/matching/profile/ProfileHero';
import { ProfilePreferences } from '../components/matching/profile/ProfilePreferences';
import { ProfileObjectives } from '../components/matching/profile/ProfileObjectives';
import { ProfileLanguages } from '../components/matching/profile/ProfileLanguages';
import { ProfileInterests } from '../components/matching/profile/ProfileInterests';
import { ProfileComments } from '../components/matching/profile/ProfileComments';
import { ProfileDetailPhotos } from '../components/matching/profile/ProfileDetailPhotos';
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal';
import * as amplitude from '@amplitude/analytics-browser';
import { openWebRedirect } from '../lib/api';
import { budgetCountries } from '../data/budget-countries';
import { translateTravelStyle } from '../utils/matching';
import { ref, deleteObject } from 'firebase/storage';
import { createNotification } from '../lib/notification-service';

interface ProfileDetailPageProps {
  isModal?: boolean;
  onModalClose?: () => void;
  profileIdOverride?: string;
  onDelete?: () => void;
  isDeletingProfile?: boolean;
}

export function ProfileDetailPage({ isModal = false, onModalClose, profileIdOverride, onDelete: externalOnDelete, isDeletingProfile: externalIsDeletingProfile }: ProfileDetailPageProps = {}) {
  const { user, userInfo, isAdmin } = useAuth();
  const { id: urlId } = useParams<{ id: string }>();
  const id = profileIdOverride || urlId;
  const navigate = useNavigate();
  const [profile, setProfile] = React.useState<TravelProfile | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showEditForm, setShowEditForm] = React.useState(false);
  const [isOwnProfile, setIsOwnProfile] = React.useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
  const [hasViewedProfile, setHasViewedProfile] = React.useState(false);
  const [isLiked, setIsLiked] = React.useState(false);
  const [isLiking, setIsLiking] = React.useState(false);
  const [isDeletingProfile, setIsDeletingProfile] = React.useState(false);
  const [deletionError, setDeletionError] = React.useState<string | null>(null);

  // Redirect to home if not authenticated (only in non-modal mode)
  React.useEffect(() => {
    if (!user && !isModal) {
      navigate('/', { replace: true });
    }
  }, [user, navigate, isModal]);

  // Get country information
  const countryInfo = React.useMemo(() => {
    if (!profile?.preferences.destination) return null;
    return budgetCountries.find(c => c.code === profile.preferences.destination);
  }, [profile?.preferences.destination]);

  // Calculate age from birthDate
  const getAge = (birthDate: any) => {
    if (!birthDate) return null;
    let date;
    try {
      date = birthDate.toDate?.() || new Date(birthDate);
    } catch (error) {
      date = new Date(birthDate);
    }
    return Math.floor((new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24 * 365.25));
  };

  // Fetch profile data
  React.useEffect(() => {
    const fetchProfile = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        const profileRef = doc(db, 'travel_profiles', id);
        const profileDoc = await getDoc(profileRef);
        
        if (!profileDoc.exists()) {
          setError('Profil non trouvé');
          return;
        }

        const profileData = profileDoc.data();
        const isOwn = profileData.userId === user?.uid;
        setIsOwnProfile(isOwn);

        // Check if current user has liked this profile
        setIsLiked(profileData.likes?.includes(user?.uid));

        // Ne pas incrémenter les vues pour son propre profil
        if (isOwn) {
          const ownerViewedAt = Timestamp.fromDate(new Date());
          await updateDoc(profileRef, {
            ownerViewedAt
          });

        } else {
          if (!isOwn && !hasViewedProfile) {
            const viewCount = (profileData.viewCount || 0) + 1;
            
            await updateDoc(profileRef, {
              viewCount
            });
  
            setHasViewedProfile(true);
            profileData.viewCount = viewCount;
          } 
        }
        
        setProfile({
          id: profileDoc.id,
          ...profileData,
          username: isOwn ? userInfo?.user?.username : profileData.username,
          isOwnProfile: isOwn
        } as TravelProfile);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError('Impossible de charger le profil');
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchProfile();
    }
  }, [id, user, userInfo?.user?.username, hasViewedProfile]);

  const handleShare = async () => {
    // Analytics.
    amplitude.track('PVT Mate Post Shared');

    try {
      if (navigator.share) {
        await navigator.share({
          title: `Profil de ${profile?.firstName}`,
          text: profile?.bio,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert('Lien copié dans le presse-papier !');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleEditProfile = () => {
    setShowEditForm(true);
  };

  const handleUpdateProfile = async (updatedData: any) => {
    try {
      if (!profile || !user) return;

      const profileRef = doc(db, 'travel_profiles', profile.id);
      await updateDoc(profileRef, {
        ...updatedData,
        updatedAt: new Date()
      });

      // Refresh profile data
      const updatedProfileDoc = await getDoc(profileRef);
      setProfile({
        id: updatedProfileDoc.id,
        ...updatedProfileDoc.data(),
        isOwnProfile: true
      } as TravelProfile);

      setShowEditForm(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Erreur lors de la mise à jour du profil');
    }
  };

  const handleDeleteProfile = async () => {
    if (!profile || !user || (!isOwnProfile && !isAdmin)) return;
    
    setIsDeletingProfile(true);
    setDeletionError(null);
    
    try {
      console.log('Starting profile deletion process');
      let photoDeletionErrors = 0;
      
      // Delete photos from storage if they exist
      if (profile.photos && profile.photos.length > 0) {
        console.log(`Found ${profile.photos.length} photos to delete`);
        
        // Create an array of delete promises
        const deletePromises = profile.photos.map(async (photoUrl, index) => {
          try {
            console.log(`Processing photo ${index + 1}/${profile.photos.length}: ${photoUrl}`);
            
            // Extract the file path from the URL
            // Format can be like: 
            // - https://storage.googleapis.com/[bucket]/PVTmates/Photos/[userId]_[timestamp].[ext]
            // - https://firebasestorage.googleapis.com/v0/b/[bucket]/o/PVTmates%2FPhotos%2F[userId]_[timestamp].[ext]?....
            
            let fileName;
            
            if (photoUrl.includes('firebasestorage.googleapis.com')) {
              // Format: https://firebasestorage.googleapis.com/v0/b/[bucket]/o/PVTmates%2FPhotos%2F[filename]?...
              const matches = photoUrl.match(/o\/([^?]+)/);
              if (matches && matches[1]) {
                fileName = decodeURIComponent(matches[1]);
                // Remove the path prefix if present
                fileName = fileName.replace('PVTmates/Photos/', '');
              } else {
                throw new Error(`Could not parse Firebase Storage URL: ${photoUrl}`);
              }
            } else {
              // Format: direct URL like https://storage.googleapis.com/bucket/PVTmates/Photos/[userId]_[timestamp].[ext]
              const parts = photoUrl.split('/');
              fileName = parts[parts.length - 1].split('?')[0]; // Remove query parameters
            }
            
            console.log(`Extracted file name: ${fileName}`);
            
            // Create a reference to the file
            const photoRef = ref(storage, `PVTmates/Photos/${fileName}`);
            
            console.log(`Attempting to delete photo: ${fileName}`);
            
            // Delete the file
            await deleteObject(photoRef);
            console.log(`Successfully deleted photo: ${fileName}`);
            return true;
          } catch (error) {
            photoDeletionErrors++;
            console.error(`Failed to delete photo ${photoUrl}:`, error);
            return false;
          }
        });
        
        // Wait for all delete operations to complete
        await Promise.all(deletePromises);
        console.log(`Photo deletion completed with ${photoDeletionErrors} errors`);
      } else {
        console.log('No photos to delete');
      }

      // Now delete the profile document
      console.log('Deleting profile document');
      const profileRef = doc(db, 'travel_profiles', profile.id);
      await deleteDoc(profileRef);
      console.log('Profile document deleted successfully');
      
      // Redirect to matching page or close modal after successful deletion
      if (isModal && onModalClose) {
        onModalClose();
      } else {
        navigate('/matching');
      }
      
      // If some photos couldn't be deleted but the profile was deleted successfully,
      // we still complete the operation but show a warning
      if (photoDeletionErrors > 0) {
        console.warn(`Profile deleted but ${photoDeletionErrors} photos couldn't be deleted`);
        // Show warning toast
        const toast = document.createElement('div');
        toast.className = 'fixed bottom-4 left-1/2 -translate-x-1/2 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 px-6 py-3 rounded-xl shadow-lg border border-amber-100 dark:border-amber-800/30 z-50 flex items-center gap-3 animate-fade-in';
        toast.innerHTML = `
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>Profil supprimé, mais certaines photos n'ont pas pu être supprimées</span>
        `;
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 5000);
      }
    } catch (error) {
      console.error('Error deleting profile:', error);
      setDeletionError('Une erreur est survenue lors de la suppression du profil. Veuillez réessayer.');
      
      // Show error toast
      const toast = document.createElement('div');
      toast.className = 'fixed bottom-4 left-1/2 -translate-x-1/2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-6 py-3 rounded-xl shadow-lg border border-red-100 dark:border-red-800/30 z-50 flex items-center gap-3 animate-fade-in';
      toast.innerHTML = `
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span>Erreur lors de la suppression du profil</span>
      `;
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 5000);
    } finally {
      setIsDeletingProfile(false);
      setShowDeleteConfirmation(false);
    }
  };

  const handleMessage = () => {
    // Analytics.
    amplitude.track('PVT Mate Post Send Message');

    if (!profile) return;

    const messageUrl = `https://pvtistes.net/forum/private.php?do=newpm&u=${profile.userId}&title=PVTmates`;
    openWebRedirect(messageUrl, userInfo);
  };

  const handleToggleLike = async () => {
    if (!user?.uid || !profile || isLiking || isOwnProfile) return;

    try {
      setIsLiking(true);
      const profileRef = doc(db, 'travel_profiles', profile.id);
      
      // Update Firestore
      if (isLiked) {
        await updateDoc(profileRef, {
          likes: arrayRemove(user.uid)
        });
        // Update local state
        setProfile(prev => {
          if (!prev) return prev;
          const updatedLikes = (prev.likes || []).filter(id => id !== user.uid);
          return { ...prev, likes: updatedLikes };
        });
        setIsLiked(false);
        
        // Analytics
        amplitude.track('PVT Mate Unlike', { profileId: profile.id });
      } else {
        await updateDoc(profileRef, {
          likes: arrayUnion(user.uid)
        });
        // Update local state
        setProfile(prev => {
          if (!prev) return prev;
          const updatedLikes = [...(prev.likes || []), user.uid];
          return { ...prev, likes: updatedLikes };
        });
        setIsLiked(true);

        if (profile.userId && userInfo?.user?.username) {
          createNotification({
            recipientId: profile.userId,
            senderId: user.uid,
            senderUsername: userInfo.user.username,
            type: 'profile_like',
            messageId: profile.id,
            messagePreview: profile.firstName,
            threadCode: profile.id,
            threadType: 'profile',
            profileId: profile.id,
          });
        }

        amplitude.track('PVT Mate Like', { profileId: profile.id });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Show error toast
      const toast = document.createElement('div');
      toast.className = 'fixed bottom-4 left-1/2 -translate-x-1/2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-6 py-3 rounded-xl shadow-lg border border-red-100 dark:border-red-800/30 z-50 flex items-center gap-3 animate-fade-in';
      toast.innerHTML = `
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span>Une erreur est survenue</span>
      `;
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 3000);
    } finally {
      setIsLiking(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col pb-mobile">
      {!isModal && (
        <Navbar
          onLogin={() => {}}
          onAdminClick={() => {}}
          onHomeClick={() => {}}
          isAdminMode={false}
        />
      )}

      <ProfileHeader
        onShare={handleShare}
        onEdit={isOwnProfile ? handleEditProfile : undefined}
        onToggleLike={!isOwnProfile ? handleToggleLike : undefined}
        isOwnProfile={isOwnProfile}
        isLiked={isLiked}
        username={profile?.username}
        userInfo={userInfo}
        isModal={isModal}
        onModalClose={onModalClose}
      />

      <main className={`flex-1 ${isModal ? '' : 'pt-32'}`}>
        {isLoading ? (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="animate-pulse space-y-8">
              <div className="aspect-[4/3] bg-gray-200 dark:bg-gray-700 rounded-2xl" />
              <div className="space-y-4">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/3" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-2/3" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/2" />
              </div>
            </div>
          </div>
        ) : error || !profile ? (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 text-center">
              <p className="text-red-600 dark:text-red-400">{error || 'Profil non trouvé'}</p>
            </div>
          </div>
        ) : (
          <>
            <ProfileHero
              profile={profile}
              isModal={isModal}
              onModalClose={onModalClose}
              onEdit={isOwnProfile ? handleEditProfile : undefined}
              onDelete={
                isModal
                  ? externalOnDelete
                  : (isOwnProfile || isAdmin) ? () => setShowDeleteConfirmation(true) : undefined
              }
              isDeletingProfile={isModal ? externalIsDeletingProfile : isDeletingProfile}
            />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 sm:-mt-20 z-10 relative">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-premium border border-gray-200/50 dark:border-gray-700/50 overflow-hidden mb-6">
                <div className="p-5 sm:p-6 border-b border-gray-100 dark:border-gray-700/50">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                    {profile.username && !isOwnProfile && (
                      <a
                        href={`https://pvtistes.net/membre/${profile.username}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          e.preventDefault();
                          openWebRedirect(`https://pvtistes.net/membre/${profile.username}/`, userInfo);
                        }}
                        className="inline-flex items-center gap-2 px-3.5 py-2 bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm font-medium"
                      >
                        <User className="w-4 h-4" />
                        <span>Profil pvtistes.net</span>
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                      </a>
                    )}

                    {!isModal && !isOwnProfile && (
                      <div className="flex items-center gap-2.5">
                        <button
                          onClick={handleToggleLike}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all text-sm font-medium ${
                            isLiked
                              ? 'text-rose-600 bg-rose-50 hover:bg-rose-100 dark:text-rose-400 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800/30'
                              : 'text-gray-600 bg-gray-50 hover:bg-gray-100 dark:text-gray-300 dark:bg-gray-700/50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'
                          }`}
                          disabled={isLiking}
                        >
                          <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
                          <span>{isLiked ? "J'aime" : "J'aime"}</span>
                        </button>
                        <button
                          onClick={handleMessage}
                          className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-colors text-sm font-medium shadow-sm"
                        >
                          <MessageSquare className="w-4 h-4" />
                          <span>Message</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-5 sm:p-6 lg:p-8 space-y-8">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      A propos
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap text-[15px] leading-relaxed">
                      {profile.bio}
                    </p>
                  </div>

                  <ProfileDetailPhotos profile={profile} />
                  <ProfilePreferences profile={profile} />
                  <ProfileObjectives profile={profile} />
                  <ProfileLanguages languages={profile.languages} />
                  <ProfileInterests interests={profile.interests} />

                  <ProfileComments
                    profile={profile}
                    currentUserId={user.uid}
                    userInfo={userInfo}
                    isAdmin={isAdmin}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      {!isModal && <Footer />}

      {/* Edit Form Modal */}
      {showEditForm && profile && (
        <CreateListingForm
          onSubmit={handleUpdateProfile}
          onClose={() => setShowEditForm(false)}
          initialData={{
            firstName: profile.firstName,
            birthDate: (profile.birthDate.toDate?.() || new Date(profile.birthDate)).toISOString().split('T')[0],
            location: profile.location,
            bio: profile.bio,
            languages: profile.languages,
            interests: profile.interests,
            preferences: {
              ...profile.preferences,
              startDate: profile.preferences.startDate.toDate?.() || new Date(profile.preferences.startDate),
              endDate: profile.preferences.endDate.toDate?.() || new Date(profile.preferences.endDate)
            },
            pvtStatus: profile.pvtStatus,
            objectives: profile.objectives || []
          }}
          isEditing={true}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteProfile}
        itemName={isOwnProfile ? "votre annonce" : "cette annonce"}
        confirmationWord="SUPPRIMER"
        description={isOwnProfile
          ? "Cette action est irréversible. Toutes les données de votre annonce seront définitivement supprimées."
          : "Cette action est irréversible. L'annonce et toutes ses données seront définitivement supprimées."
        }
      />

      {/* Error Modal for Deletion */}
      {deletionError && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Erreur de suppression
                </h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {deletionError}
              </p>
              <div className="flex justify-end">
                <button
                  onClick={() => setDeletionError(null)}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}